export { DeductionKey } from "@boo/shared";
import { DeductionKey } from "@boo/shared";

export const ALL_DEDUCTION_KEYS: DeductionKey[] = Object.values(
  DeductionKey,
) as DeductionKey[];

export interface TaxYearInput {
  // 40(1) Employment income
  employmentIncome: number;

  // 40(8) Freelance / business income
  freelanceRevenue: number;
  freelanceMethod: "standard" | "actual";
  freelanceActualExpenses: number;

  // Withholding tax credit
  withholdingTax: number;

  // Personal & family allowances
  hasSpouse: boolean;
  childrenCount: number;
  parentSupportCount: number; // 0–4
  disabledSupportCount: number;

  // Insurance
  lifeInsurance: number;
  healthInsurance: number;
  parentHealthInsurance: number;
  sso: number;

  // Retirement / savings
  pvd: number;
  rmf: number;
  ssf: number;
  tesg: number;

  // Housing
  homeLoanInterest: number;
  newHomeEReceipt: number; // BE 2568 only

  // Donations & spending
  easyEReceipt: number; // paid amount; BE 2568 only
  educationDonation: number; // paid amount (double deduction)
  politicalDonation: number;
}

export const defaultTaxYearInput: TaxYearInput = {
  employmentIncome: 0,
  freelanceRevenue: 0,
  freelanceMethod: "standard",
  freelanceActualExpenses: 0,
  withholdingTax: 0,
  hasSpouse: false,
  childrenCount: 0,
  parentSupportCount: 0,
  disabledSupportCount: 0,
  lifeInsurance: 0,
  healthInsurance: 0,
  parentHealthInsurance: 0,
  sso: 0,
  pvd: 0,
  rmf: 0,
  ssf: 0,
  tesg: 0,
  homeLoanInterest: 0,
  newHomeEReceipt: 0,
  easyEReceipt: 0,
  educationDonation: 0,
  politicalDonation: 0,
};

export interface BracketResult {
  from: number;
  to: number | null; // null = unbounded
  rate: number;
  taxableInBracket: number;
  taxInBracket: number;
}

export type TipPriority = "high" | "medium" | "low";

export interface TipItem {
  id: string;
  title: string;
  description: string;
  potentialSaving: number;
  priority: TipPriority;
}

export interface TaxResult {
  totalGrossIncome: number;

  // Expense deductions
  employmentExpenseDeduction: number;
  freelanceExpenseDeduction: number;
  totalExpenseDeduction: number;
  incomeAfterExpense: number;

  // Personal / family
  personalAllowance: number;
  spouseAllowance: number;
  childrenAllowance: number;
  parentSupportAllowance: number;
  disabledSupportAllowance: number;

  // Insurance
  lifeInsuranceDeduction: number;
  healthInsuranceDeduction: number;
  parentHealthInsuranceDeduction: number;
  ssoDeduction: number;

  // Retirement
  pvdDeduction: number;
  rmfDeduction: number;
  ssfDeduction: number;
  retirementCombined: number; // capped at 500k
  tesgDeduction: number;

  // Housing
  homeLoanInterestDeduction: number;
  newHomeEReceiptDeduction: number;

  // Donations
  easyEReceiptDeduction: number;
  educationDonationDeduction: number;
  politicalDonationDeduction: number;

  totalDeductions: number;
  netTaxableIncome: number;

  // Tax
  brackets: BracketResult[];
  grossTax: number;
  withholdingCredit: number;
  netTax: number;
  refund: number;
  effectiveRate: number; // grossTax / totalGrossIncome

  tips: TipItem[];
}

// CE 2023–2040 (BE 2566–2583)
export const TAX_YEAR_CE_MIN = 2026;
export const TAX_YEAR_CE_MAX = 2040;
export const CE_TO_BE = 543;
